import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export type RequestStatus = 'requested' | 'in_progress' | 'needs_info' | 'ready' | 'declined';
/** What a leader sees: Checking lasts until an admin publishes. */
export type RequestProgress = 'requested' | 'building' | 'checking' | 'needs_info' | 'ready' | 'declined';

export type BuildStage = 'queued' | 'reading' | 'building' | 'testing' | 'checking' | 'ready' | 'needs_info' | 'failed';

export interface BuildCheck {
  key: string;
  label: string;
  pass: boolean;
  note: string;
  indicators: string[];
}

export interface BuildTest {
  sampleId: string;
  quality: 'strong' | 'typical' | 'weak';
  subject: string;
  yearGroup: string;
  overallScore: number | null;
  summary: string;
  indicators: {
    area: string;
    name: string;
    score: number | null;
    notSeen?: boolean;
    evidence: boolean;
    quote: string | null;
    feedback: string;
    nextStep: string | null;
  }[];
  error?: string;
}

/** The AI builder's latest attempt at a request, for the admin's review. */
export interface FrameworkBuild {
  id: string;
  stage: BuildStage;
  round: number;
  frameworkId: string | null;
  frameworkName: string | null;
  frameworkStatus: string | null;
  summary: string | null;
  scorecard: BuildCheck[];
  tests: BuildTest[];
  sourceMap: { area: string; indicator: string; description?: string; source: string }[];
  notIncluded: { item: string; reason: string }[];
  questions: string[];
  error: string | null;
  costUsd: number;
  createdAt: string;
  finishedAt: string | null;
}

export interface FrameworkRequest {
  id: string;
  name: string;
  purpose: string | null;
  details: string | null;
  /** Areas and indicators the leader typed in, if they did. */
  structure: TypedStructure | null;
  link: string | null;
  fileName: string | null;
  hasFile: boolean;
  status: RequestStatus;
  progress: RequestProgress;
  fileDeleted: boolean;
  frameworkId: string | null;
  frameworkName: string | null;
  adminNote: string | null;
  requestedBy: string | null;
  createdAt: string;
  updatedAt: string;
  schoolId?: string;
  schoolName?: string;
  fileUrl?: string | null;
  buildStage?: BuildStage | null;
  build?: FrameworkBuild | null;
}

export const STATUS_LABEL: Record<RequestStatus, string> = {
  requested: 'Requested',
  in_progress: 'Being built',
  needs_info: 'Asked for more',
  ready: 'Ready',
  declined: 'Declined',
};

export const PROGRESS_LABEL: Record<RequestProgress, string> = {
  requested: 'Requested',
  building: 'Being built',
  checking: 'Checking',
  needs_info: 'We need a bit more',
  ready: 'Ready',
  declined: 'Declined',
};

/** A request that's still open: only one at a time per organisation. */
export const isOpenRequest = (r: FrameworkRequest) => r.status === 'requested' || r.status === 'in_progress' || r.status === 'needs_info';

// Leaders
export const getMyRequests = async () => (await api<{ data: FrameworkRequest[] }>('/frameworks/requests')).data;
export const withdrawRequest = (id: string) => api(`/frameworks/requests/${id}`, { method: 'DELETE' });

/** Uploads a document straight to storage with a short-lived signed URL, then returns its key. */
export async function uploadRequestDocument(file: File, requestId?: string): Promise<{ fileKey: string; fileName: string }> {
  const { uploadUrl, fileKey } = await api<{ uploadUrl: string; fileKey: string }>('/frameworks/requests/upload-url', {
    method: 'POST',
    body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream', size: file.size, requestId }),
  });
  let res: Response;
  try {
    res = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
  } catch {
    // Most likely the storage bucket hasn't been opened to the web app yet.
    throw new Error('Uploading isn’t available right now. Add a link to the document or paste it in instead.');
  }
  if (!res.ok) throw new Error('The upload didn’t finish. Try again, or add a link instead.');
  return { fileKey, fileName: file.name };
}

export interface TypedStructure {
  areas: { name: string; indicators: { name: string; description: string }[] }[];
}

export const createRequest = (body: {
  name: string;
  purpose?: string;
  details?: string;
  structure?: TypedStructure;
  link?: string;
  fileKey?: string;
  fileName?: string;
}) => api<FrameworkRequest>('/frameworks/requests', { method: 'POST', body: JSON.stringify(body) });

/** Add what we asked for to a request; it's then built again. */
export const addToRequest = (id: string, body: { details?: string; link?: string; fileKey?: string; fileName?: string }) =>
  api<FrameworkRequest>(`/frameworks/requests/${id}/more`, { method: 'POST', body: JSON.stringify(body) });

// Admins
export interface LibraryRow {
  id: string;
  name: string;
  description: string;
  category: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  listed: number;
  school_id: string | null;
  owner_name: string | null;
  updated_at: string;
  domains: number;
  indicators: number;
  shared_with: number;
  default_for: number;
  /** Organisations that can use it, each counted once; absent on older APIs. */
  used_by?: number;
}

export interface Indicator {
  id: string;
  name: string;
  description: string;
  sortOrder: number;
}
export interface Domain {
  id: string;
  name: string;
  sortOrder: number;
  indicators: Indicator[];
}
export interface FrameworkDetail {
  id: string;
  name: string;
  description: string;
  category: string;
  status: LibraryRow['status'];
  listed: number;
  school_id: string | null;
  domains: Domain[];
  sharedWithSchools: { schoolId: string; name: string; inviteCode: string; grantedAt: string | null; isDefault: boolean }[];
  /** Who sent a school-built framework for review; older APIs leave it out. */
  submittedBy?: { name: string; email: string; schoolName: string | null } | null;
}

export const getLibrary = async () => (await api<{ data: LibraryRow[] }>('/admin/frameworks')).data;
export const getFramework = (id: string) => api<FrameworkDetail>(`/frameworks/${id}`);
/** Copy a framework into a new draft made for one organisation, to tweak for them. */
export const duplicateFramework = (id: string, schoolId: string, name?: string) =>
  api<{ id: string; name: string }>(`/admin/frameworks/${id}/duplicate`, { method: 'POST', body: JSON.stringify({ schoolId, name }) });
export const createFramework = (name: string, description: string) =>
  api<{ id: string }>('/frameworks', { method: 'POST', body: JSON.stringify({ name, description, schoolId: null, draft: true }) });
export const updateFramework = (id: string, body: Partial<{ name: string; description: string; listed: boolean; status: 'approved' | 'draft' }>) =>
  api(`/frameworks/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteFramework = (id: string) => api(`/frameworks/${id}`, { method: 'DELETE' });
export const addDomain = (id: string, name: string) => api<{ id: string }>(`/frameworks/${id}/domains`, { method: 'POST', body: JSON.stringify({ name }) });
export const updateDomain = (id: string, domainId: string, body: Partial<{ name: string; sortOrder: number }>) =>
  api(`/frameworks/${id}/domains/${domainId}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteDomain = (id: string, domainId: string) => api(`/frameworks/${id}/domains/${domainId}`, { method: 'DELETE' });
export const addIndicator = (id: string, domainId: string, name: string, description: string) =>
  api<{ id: string }>(`/frameworks/${id}/domains/${domainId}/indicators`, { method: 'POST', body: JSON.stringify({ name, description }) });
export const updateIndicator = (id: string, domainId: string, indicatorId: string, body: Partial<{ name: string; description: string; sortOrder: number }>) =>
  api(`/frameworks/${id}/domains/${domainId}/indicators/${indicatorId}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteIndicator = (id: string, domainId: string, indicatorId: string) =>
  api(`/frameworks/${id}/domains/${domainId}/indicators/${indicatorId}`, { method: 'DELETE' });
/** 'ifNone' makes it their default only when they don't have one. Calling it again for a school with access changes its default. */
export const grantAccess = (id: string, schoolId: string, setAsDefault: boolean | 'ifNone') =>
  api<{ name: string; setAsDefault: boolean }>(`/frameworks/${id}/access`, { method: 'POST', body: JSON.stringify({ schoolId, setAsDefault }) });
export const revokeAccess = (id: string, schoolId: string) => api<{ clearedDefault?: boolean }>(`/frameworks/${id}/access/${schoolId}`, { method: 'DELETE' });

/** A framework a school built itself (older app builds) and sent for review. */
export interface Submission {
  id: string;
  name: string;
  school_name: string | null;
  submitted_by_name: string | null;
  updated_at: string;
}
export const getSubmissions = async () => (await api<{ data: Submission[] }>('/frameworks/reviews/pending')).data;
/** Approving publishes it and makes it the school's default; sending back needs a note. Either way the leader is told. */
export const reviewSubmission = (id: string, body: { decision: 'approve' } | { decision: 'reject'; reason: string }) =>
  api(`/frameworks/${id}/review`, { method: 'POST', body: JSON.stringify(body) });

/** How many things are waiting on an admin: new requests plus school-built frameworks to review. */
export function useFrameworkQueueCount(enabled = true) {
  const requests = useQuery({ queryKey: ['admin-fw-requests', 'requested'], queryFn: () => getAllRequests('requested'), enabled });
  const submissions = useQuery({ queryKey: ['admin-fw-submissions'], queryFn: getSubmissions, enabled });
  return (requests.data?.length ?? 0) + (submissions.data?.length ?? 0);
}

export const getAllRequests = async (status?: string) =>
  (await api<{ data: FrameworkRequest[] }>(`/admin/framework-requests${status ? `?status=${status}` : ''}`)).data;
export const getRequest = (id: string) => api<FrameworkRequest>(`/admin/framework-requests/${id}`);
export const updateRequest = (id: string, body: Partial<{ status: RequestStatus; adminNote: string; frameworkId: string | null }>) =>
  api<FrameworkRequest>(`/admin/framework-requests/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
/** The email and phone notification a status change would send the leader. Sends nothing. */
export type LeaderMessage = { subject: string; push: { title: string; body: string }; html: string };
export const previewLeaderMessage = (id: string, status: 'ready' | 'needs_info' | 'declined', note: string) =>
  api<LeaderMessage>(`/admin/framework-requests/${id}/preview`, { method: 'POST', body: JSON.stringify({ status, note }) });
export const buildRequest = (id: string) => api<FrameworkRequest>(`/admin/framework-requests/${id}/build`, { method: 'POST' });
export const publishBuiltFramework = (id: string) => api<FrameworkRequest>(`/admin/framework-requests/${id}/publish`, { method: 'POST' });
